import React, { useEffect, useRef, useState } from 'react';
import { Avatar, Box, Button, Grid, Paper, Typography, useMediaQuery, Chip } from '@mui/material';
import { styled, useTheme, ThemeProvider, createTheme } from '@mui/material/styles';
import { AccessTime, LocationOn, Person } from '@mui/icons-material';
import { COLORS } from 'colors/color';
import { useNavigate } from 'react-router-dom';
import EventBody from './EventBody';

// Google Calendar theme for events
const googleEventTheme = createTheme({
  palette: {
    primary: {
      main: '#1a73e8',
      light: '#e8f0fe',
      dark: '#1557b0',
    },
    secondary: {
      main: '#34a853',
      light: '#e6f4ea',
    },
    background: {
      default: '#ffffff',
      paper: '#ffffff',
    },
    grey: {
      50: '#f8f9fa',
      100: '#f1f3f4',
      200: '#e8eaed',
      300: '#dadce0',
      400: '#bdc1c6',
      500: '#9aa0a6',
      600: '#80868b',
      700: '#5f6368',
      800: '#3c4043',
      900: '#202124',
    },
    text: {
      primary: '#3c4043',
      secondary: '#5f6368',
    },
  },
  typography: {
    fontFamily: '"Google Sans", "Roboto", "Arial", sans-serif',
  },
});

const GoogleEventCard = styled(Paper)(({ theme, eventColor }) => ({
  position: 'relative',
  padding: '8px 12px',
  margin: '2px 0',
  borderRadius: '4px',
  borderLeft: `4px solid ${eventColor || '#1a73e8'}`,
  backgroundColor: eventColor ? `${eventColor}15` : '#e8f0fe',
  boxShadow: '0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.24)',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  overflow: 'hidden',
  '&:hover': {
    transform: 'translateY(-1px)',
    boxShadow: '0 2px 8px rgba(0,0,0,0.15), 0 2px 4px rgba(0,0,0,0.12)',
    borderLeftWidth: '5px',
  },
}));

const EventDetails = styled('div')(() => ({
    position: 'absolute',
    bottom: '120%',
    left: '50%',
    transform: 'translateY(0) translateX(-50%)',
    opacity: 0,
    visibility: 'hidden',
    transition: 'all 0.3s ease',
    zIndex: 1000,
    minWidth: '320px',
    borderRadius: '12px',
    marginBottom: '8px',
    pointerEvents: 'none'
  }));
const EventItem = styled("li")(({ theme }) => ({
  marginBottom: "13px",
  borderRadius: "17px",
  padding: "16px",
  position: "relative",
  overflow: "hidden",
  boxShadow: "0 4px 15px rgba(0,0,0,0.1)",
  transition: "transform 0.3s ease, box-shadow 0.3s ease",
  backgroundColor: "#fff",
 
  "&:hover": {
    transform: "translateY(-5px)",
    boxShadow: "0 6px 20px rgba(0,0,0,0.15)",
  },
}));

const StyledEventContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  display: 'inline-block',
  '&:hover .event-details': {
    opacity: 1,
    visibility: 'visible',
    pointerEvents: 'auto',
    transform: 'translateY(-8px)'
  }
}));

const StyledEventDetails = styled(Box)(({ theme }) => ({
  position: 'absolute',
  opacity: 0,
  visibility: 'hidden',
  transition: 'all 0.3s ease',
  zIndex: 1000,
  minWidth: '300px',
  borderRadius: '8px',
  marginBottom: '8px',
  pointerEvents: 'none',
  transform: 'translateY(0)',
}));

const EventContainer = ({ children, event, renderBody }) => {
  const [popupPosition, setPopupPosition] = useState({ top: 0, left: 0 });
  const containerRef = useRef(null);
  const popupRef = useRef(null);

  useEffect(() => {
    const updatePosition = () => {
      if (!containerRef.current || !popupRef.current) return;
      
      const containerRect = containerRef.current.getBoundingClientRect();
      const popupRect = popupRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      // Initial position (centered above)
      let top = -popupRect.height - 8;
      let left = -(popupRect.width / 2);

      // Adjust for right boundary
      if (containerRect.left + left + popupRect.width > viewportWidth) {
        left = viewportWidth - (containerRect.left + popupRect.width) - 16;
      }

      // Adjust for left boundary
      if (containerRect.left + left < 0) {
        left = -containerRect.left + 16;
      }

      // Adjust for top boundary
      if (containerRect.top + top < 0) {
        // Show below instead of above
        top = containerRect.height + 8;
      }

      setPopupPosition({ top, left });
    };

    window.addEventListener('resize', updatePosition);
    updatePosition();

    return () => window.removeEventListener('resize', updatePosition);
  }, []);

  return (
    <StyledEventContainer ref={containerRef}>
      {renderBody(event)}
      <StyledEventDetails
        ref={popupRef}
        className="event-details"
        sx={{
          top: popupPosition.top,
          left: popupPosition.left,
        }}
      >
        {children}
      </StyledEventDetails>
    </StyledEventContainer>
  );
};

const EventContent = ({ event }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const handleSendMessage = () => {
    console.log("Send message for event:", event);
    navigate("/talent/Chat/", {
      state: { candidate: event.extendedProps?.candidate },
    });
  };

  const cutText = (text, length) => {
    if (text && typeof text === "string") {
      return text.length > length ? `${text.substr(0, length)}...` : text;
    }
    return text || "";
  };

  const formatDate = (dateString) => {
    const options = { year: "numeric", month: "short", day: "numeric" };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  const getDateDifference = (startDate) => {
    const currentDate = new Date();
    const eventStartDate = new Date(startDate);
    const differenceInTime = eventStartDate - currentDate;
    return Math.ceil(differenceInTime / (1000 * 3600 * 24));
  };

  const getTitleColor = (days) => {
    if (days > 7) return "#34A853";
    if (days > 3) return "#FFA800";
    return "#FF0000";
  };

  const differenceInDays = getDateDifference(event.start);
  const titleColor = getTitleColor(differenceInDays);
  const candidate = event.extendedProps?.candidate;
  const candidateName = candidate?.full_name || "No name available";
  const candidateImage = candidate?.candidate_image;

  const renderEventDetails = () => (
    <ThemeProvider theme={googleEventTheme}>
      <Paper
        onClick={handleSendMessage}
        sx={{
          width: {
            xs: '280px',
            sm: '320px',
            md: '380px'
          },
          p: 3,
          borderRadius: 3,
          boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
          border: '1px solid',
          borderColor: 'grey.200',
          backgroundColor: 'background.paper',
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 12px 40px rgba(0,0,0,0.15)',
          },
        }}
      >
        {/* Header with event type and status */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Chip
            label={cutText(event.title, 20)}
            sx={{
              backgroundColor: titleColor,
              color: 'white',
              fontWeight: 500,
              fontSize: '0.75rem',
              fontFamily: '"Google Sans", "Roboto", "Arial", sans-serif',
            }}
          />
          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
              fontWeight: 500,
              fontSize: '0.6875rem',
            }}
          >
            {differenceInDays > 0 ? `In ${differenceInDays} days` : 'Today'}
          </Typography>
        </Box>

        {/* Candidate info */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Avatar
            sx={{
              bgcolor: 'primary.light',
              width: 48,
              height: 48,
              border: '2px solid',
              borderColor: 'primary.main',
              mr: 2,
            }}
            src={candidateImage}
          >
            <Person sx={{ color: 'primary.main' }} />
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="h6"
              sx={{
                fontSize: '1rem',
                fontFamily: '"Google Sans", "Roboto", "Arial", sans-serif',
                fontWeight: 600,
                color: 'text.primary',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {candidateName}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                fontSize: '0.875rem',
                fontFamily: '"Google Sans", "Roboto", "Arial", sans-serif',
              }}
            >
              Interview Candidate
            </Typography>
          </Box>
        </Box>

        {/* Event details */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
          <AccessTime sx={{ fontSize: 16, color: 'text.secondary', mr: 1 }} />
          <Typography
            variant="body2"
            sx={{
              color: 'text.primary',
              fontSize: '0.875rem',
              fontFamily: '"Google Sans", "Roboto", "Arial", sans-serif',
            }}
          >
            {formatDate(event.start)} at {event.start.toLocaleTimeString('en-US', {
              hour: 'numeric',
              minute: '2-digit',
              hour12: true
            })}
          </Typography>
        </Box>

        {/* Action button */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
          <Button
            variant="contained"
            size="small"
            sx={{
              backgroundColor: 'primary.main',
              color: 'white',
              fontFamily: '"Google Sans", "Roboto", "Arial", sans-serif',
              textTransform: 'none',
              fontWeight: 500,
              borderRadius: 2,
              px: 3,
              '&:hover': {
                backgroundColor: 'primary.dark',
              },
            }}
          >
            Join Interview
          </Button>
        </Box>
      </Paper>
    </ThemeProvider>
  );

  // Simple event body for calendar display
  const renderSimpleEventBody = () => {
    const duration = (event.end - event.start) / (1000 * 60); // duration in minutes
    const isShort = duration < 60;

    return (
      <ThemeProvider theme={googleEventTheme}>
        <GoogleEventCard
          eventColor={titleColor}
          onClick={handleSendMessage}
          sx={{
            height: '100%',
            minHeight: isShort ? '24px' : '48px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
          }}
        >
          <Typography
            variant="caption"
            sx={{
              fontWeight: 600,
              fontSize: isShort ? '0.6875rem' : '0.75rem',
              lineHeight: 1.2,
              color: titleColor,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: isShort ? 'nowrap' : 'normal',
              display: '-webkit-box',
              WebkitLineClamp: isShort ? 1 : 2,
              WebkitBoxOrient: 'vertical',
              fontFamily: '"Google Sans", "Roboto", "Arial", sans-serif',
            }}
          >
            {cutText(event.title, isShort ? 20 : 30)}
          </Typography>
          {!isShort && (
            <Typography
              variant="caption"
              sx={{
                fontSize: '0.6875rem',
                color: 'text.secondary',
                mt: 0.25,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                fontFamily: '"Google Sans", "Roboto", "Arial", sans-serif',
              }}
            >
              {candidateName}
            </Typography>
          )}
        </GoogleEventCard>
      </ThemeProvider>
    );
  };

  return (
    <EventContainer
      event={event}
      renderBody={() => renderSimpleEventBody()}
    >
      {renderEventDetails()}
    </EventContainer>
  );
};

export default EventContent;